import React, { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import AiBanner from './AiBanner';
import { ApplyVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';
import './effectBlocks.css';

const DEFAULT_PARAMS = { zoom: 1.5, x: 0.5, y: 0.5 };

/**
 * ZoomBlockEditor — phase-2 effect block: a static "punch-in". Drag the yellow
 * window to choose the focal point, set the zoom factor, then compile via
 * ApplyBlocks -> crop_tool + scale_tool (deterministic, no zoompan).
 *
 * Props: videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, onApplied, onClose
 */
function ZoomBlockEditor({ videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, aiSuggestion, onApplied, onClose }) {
    const [params, setParams] = useState(() => ({ ...DEFAULT_PARAMS, ...(aiSuggestion?.params || {}) }));
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const [measured, setMeasured] = useState(() => (
        Number(mediaInfo?.width) > 0 && Number(mediaInfo?.height) > 0 ? mediaInfo : null
    ));

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const block = useMemo(() => ({ id: 'zoom-1', type: 'zoom', source: sourceFileName, params }), [params, sourceFileName]);

    const update = (patch) => setParams((prev) => ({ ...prev, ...patch }));

    const canApply = Boolean(
        videoWorkspaceId && sourceFileName &&
        Number(measured?.width) > 0 && Number(measured?.height) > 0 &&
        (params.zoom || 0) > 1
    );

    const handleApply = async () => {
        if (!canApply) {
            setError('Set a zoom above 1× and make sure the clip has loaded.');
            return;
        }
        setApplying(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const outBlock = {
                id: 'zoom-1',
                type: 'zoom',
                source: sourceFileName,
                startTime: 0,
                endTime: null,
                params,
            };
            const media = {
                [sourceFileName]: {
                    width: Math.round(Number(measured.width)),
                    height: Math.round(Number(measured.height)),
                    duration: Number(measured.duration) || 0,
                },
            };
            const res = await ApplyVideoWorkspaceBlocks(idToken, videoWorkspaceId, [outBlock], media);
            if (res.status === 200) {
                onApplied?.(res.myJson);
            } else {
                setError(res.myJson?.detail || 'Failed to apply the zoom.');
            }
        } catch (err) {
            setError('Error applying edit: ' + err.message);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="eb-editor">
            <AiBanner rationale={aiSuggestion?.rationale} />
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[block]}
                    selectedId="zoom-1"
                    onChange={(_id, next) => setParams(next)}
                    onSelect={() => {}}
                    onMeta={setMeasured}
                />
                <div className="eb-controls">
                    <h5>Zoom</h5>
                    <p className="eb-hint">Drag the window to pick the focal point. A punch-in keeps that framing for the whole clip.</p>

                    <label className="eb-ctl">
                        <span>Zoom <em>{Math.round((params.zoom || 1) * 100) / 100}×</em></span>
                        <input
                            type="range" min="1.1" max="3" step="0.05"
                            value={params.zoom || 1.5}
                            onChange={(e) => update({ zoom: Number(e.target.value) })}
                        />
                    </label>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button type="button" className="eb-btn primary" disabled={!canApply || applying} onClick={handleApply}>
                            {applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ZoomBlockEditor;
